import { useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Timer,
  Gauge,
  Slack,
  Repeat,
  ExternalLink,
  Users,
  Check,
  ArrowRight,
  ArrowLeft,
  Trash2,
  Sun,
  Calendar,
  Link2,
  Bot,
} from "lucide-react";
import type { ITask } from "../types";
import { useStore } from "../store/useStore";
import TaskContextMenu, { type ContextMenuItem } from "./TaskContextMenu";
import AddPrerequisiteModal from "./AddPrerequisiteModal";

interface TaskCardProps {
  task: ITask;
  showMoveToToday?: boolean;
  showMoveToBacklog?: boolean;
  showComplete?: boolean;
  showStatus?: boolean;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
}

function formatDueDate(dateStr: string): { label: string; color: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + "T00:00:00");
  const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, color: "text-red-500" };
  if (diffDays === 0) return { label: "Today", color: "text-amber-500" };
  if (diffDays === 1) return { label: "Tomorrow", color: "text-amber-500" };
  if (diffDays <= 7) {
    const dayName = due.toLocaleDateString("en-US", { weekday: "short" });
    return { label: dayName, color: "text-muted-foreground" };
  }
  const formatted = due.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return { label: formatted, color: "text-muted-foreground" };
}

function formatFullDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function TaskCard({
  task,
  showMoveToToday = false,
  showMoveToBacklog = false,
  showComplete = false,
  showStatus = false,
}: TaskCardProps) {
  const history = useHistory();
  const { moveToToday, moveToBacklog, completeTask, deleteTask } = useStore();
  const tags = useStore((s) => s.tags);
  const sharedTags = useStore((s) => s.sharedTags);
  const user = useStore((s) => s.user);
  const dependencies = useStore((s) => s.dependencies);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [prereqModalOpen, setPrereqModalOpen] = useState(false);

  const prereqCount = dependencies.filter((d) => d.dependent_uuid === task.uuid).length;
  const dependentCount = dependencies.filter((d) => d.prerequisite_uuid === task.uuid).length;
  const hasChainBadge = prereqCount > 0 || dependentCount > 0;

  const displayTitle = task.title || task.raw_input;
  const isEnriching = !task.ai_enriched && !task.title;
  const isShared = user ? task.user_uuid !== user.id : false;

  // Tag info
  const ownTag = task.tag_id ? tags.find((t) => t.id === task.tag_id) : null;
  const sharedTag = task.tag_id
    ? sharedTags.find((st) => st.tag_id === task.tag_id)
    : null;
  const tagName = ownTag?.name || sharedTag?.tag_name;
  const tagColor = ownTag?.color || sharedTag?.tag_color;

  // Determine if metadata row has content
  const hasMetadata =
    (task.urgency && task.urgency !== "low") ||
    (task.importance && task.importance !== "low") ||
    tagName ||
    isShared ||
    task.source !== "manual" ||
    task.url ||
    task.due_date ||
    hasChainBadge;

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isShared) return;
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({ x: e.clientX, y: e.clientY });
    },
    [isShared]
  );

  // Build context menu items
  const menuItems: ContextMenuItem[] = [];
  if (showMoveToToday) {
    menuItems.push({
      type: "action",
      label: "Move to Today",
      icon: <ArrowRight className="w-4 h-4" />,
      onClick: () => moveToToday(task.uuid),
    });
  }
  if (showMoveToBacklog) {
    menuItems.push({
      type: "action",
      label: "Move to Backlog",
      icon: <ArrowLeft className="w-4 h-4" />,
      onClick: () => moveToBacklog(task.uuid),
    });
  }
  if (showComplete) {
    menuItems.push({
      type: "action",
      label: "Complete",
      icon: <Check className="w-4 h-4" />,
      onClick: () => completeTask(task.uuid),
    });
  }
  if (menuItems.length > 0) {
    menuItems.push({ type: "separator" });
  }
  menuItems.push({
    type: "action",
    label: "Add prerequisite",
    icon: <Link2 className="w-4 h-4" />,
    onClick: () => setPrereqModalOpen(true),
  });
  menuItems.push({
    type: "action",
    label: "Delete",
    icon: <Trash2 className="w-4 h-4" />,
    onClick: () => deleteTask(task.uuid),
    variant: "destructive",
  });

  return (
    <>
      <div
        data-testid="task-card"
        className="group relative bg-surface border border-border rounded-lg overflow-hidden cursor-pointer transition-shadow shadow-card hover:shadow-card-hover"
        onClick={() => history.push(`/tasks/${task.uuid}`)}
        onContextMenu={handleContextMenu}
      >
        {/* Tag color left border */}
        {tagColor && (
          <div
            data-testid="tag-color-border"
            className="absolute left-0 top-0 bottom-0 w-[3px]"
            style={{ backgroundColor: tagColor }}
          />
        )}

        <div className={`py-2.5 pr-3 ${tagColor ? "pl-4" : "pl-3"}`}>
          <div className="flex items-start gap-2.5">
            {/* Status indicator */}
            {showStatus && task.status === "today" && (
              <span
                className="mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10"
                title="On Today"
              >
                <Sun className="w-3 h-3 text-primary" />
              </span>
            )}

            {/* Complete checkbox */}
            {showComplete && !isShared && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  completeTask(task.uuid);
                }}
                className="mt-px w-[18px] h-[18px] rounded-full border-[1.5px] border-muted-foreground/30 hover:border-primary hover:bg-primary/10 transition-colors flex-shrink-0 flex items-center justify-center group/check"
              >
                <Check className="w-2.5 h-2.5 text-transparent group-hover/check:text-primary transition-colors" />
              </button>
            )}

            <div className="flex-1 min-w-0">
              {isEnriching ? (
                <div className="space-y-1.5">
                  <p className="text-sm text-muted-foreground truncate">
                    {task.raw_input}
                  </p>
                  <div className="h-3.5 w-3/4 shimmer rounded" />
                  <div className="flex items-center gap-1.5 text-xs text-primary/70">
                    <span className="inline-block w-2.5 h-2.5 shimmer rounded-full flex-shrink-0" />
                    AI is processing...
                  </div>
                </div>
              ) : (
                <>
                  {/* Title + created_at */}
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-foreground truncate">
                      {displayTitle}
                    </h3>
                    <span
                      className="text-[11px] text-muted-foreground/50 flex-shrink-0 ml-auto tabular-nums"
                      title={formatFullDate(task.created_at)}
                    >
                      {timeAgo(task.created_at)}
                    </span>
                  </div>

                  {/* Description */}
                  {task.description && (
                    <p className="text-[13px] text-muted-foreground mt-0.5 line-clamp-1 leading-snug">
                      {task.description}
                    </p>
                  )}

                  {/* Metadata row */}
                  {hasMetadata && (
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {/* Urgency: Timer icon + level text */}
                      {task.urgency && task.urgency !== "low" && (
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                            task.urgency === "high"
                              ? "text-red-500"
                              : "text-amber-500"
                          }`}
                        >
                          <Timer className="w-3 h-3" />
                          {task.urgency === "high" ? "High" : "Med"}
                        </span>
                      )}

                      {/* Importance: Gauge icon + level text */}
                      {task.importance && task.importance !== "low" && (
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                            task.importance === "high"
                              ? "text-orange-500"
                              : "text-violet-500"
                          }`}
                        >
                          <Gauge className="w-3 h-3" />
                          {task.importance === "high" ? "High" : "Med"}
                        </span>
                      )}

                      {/* Separator between priority icons and labels */}
                      {((task.urgency && task.urgency !== "low") ||
                        (task.importance && task.importance !== "low")) &&
                        (tagName || isShared || task.source !== "manual" || task.url || task.due_date || hasChainBadge) && (
                          <span className="w-px h-3 bg-border" />
                        )}

                      {/* Tag name */}
                      {tagName && tagColor && (
                        <span
                          className="text-[11px] font-medium"
                          style={{ color: tagColor }}
                        >
                          {tagName}
                        </span>
                      )}

                      {/* Shared indicator */}
                      {isShared && (
                        <span title="Shared task" className="flex items-center">
                          <Users className="w-3 h-3 text-blue-500" />
                        </span>
                      )}

                      {/* Source icons */}
                      {task.source === "slack" && (
                        <Slack className="w-3 h-3 text-muted-foreground/50" />
                      )}
                      {task.source === "recurring" && (
                        <Repeat className="w-3 h-3 text-muted-foreground/50" />
                      )}
                      {task.source === "agent" && (
                        <span title={task.source_agent?.client_name ?? "Created by an AI agent"} className="flex items-center">
                          <Bot className="w-3 h-3 text-muted-foreground/50" />
                        </span>
                      )}

                      {/* URL link */}
                      {task.url && (
                        <a
                          href={task.url}
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[11px] text-primary/60 hover:text-primary transition-colors"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate max-w-[100px]">
                            {(() => {
                              try {
                                return new URL(task.url!).hostname.replace(
                                  "www.",
                                  ""
                                );
                              } catch {
                                return task.url!.slice(0, 20);
                              }
                            })()}
                          </span>
                        </a>
                      )}

                      {/* Due date */}
                      {task.due_date && (() => {
                        const { label, color } = formatDueDate(task.due_date);
                        return (
                          <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`}>
                            <Calendar className="w-3 h-3" />
                            {label}
                          </span>
                        );
                      })()}

                      {/* Dependency chain badges */}
                      {prereqCount > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary/70"
                          title={`Has ${prereqCount} prerequisite${prereqCount === 1 ? "" : "s"}`}
                        >
                          <Link2 className="w-3 h-3" />
                          {prereqCount}
                        </span>
                      )}
                      {dependentCount > 0 && prereqCount === 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-muted-foreground"
                          title={`Prerequisite of ${dependentCount} task${dependentCount === 1 ? "" : "s"}`}
                        >
                          <Link2 className="w-3 h-3" />
                          Prereq
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {contextMenu && (
        <TaskContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={menuItems}
          onClose={() => setContextMenu(null)}
        />
      )}

      {!isShared && (
        <AddPrerequisiteModal
          isOpen={prereqModalOpen}
          onClose={() => setPrereqModalOpen(false)}
          dependentTask={task}
        />
      )}
    </>
  );
}
